import { ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QualityScoreMeterProps {
  /** Score **on the 0–10 wire scale** before content enrichment — the component
   *  multiplies it ×10 itself for the 0–100 readout. Pass a pre-scaled 0–100
   *  value and it renders ten times too large. */
  beforeScore: number;
  /** Score on the same 0–10 scale as {@link beforeScore}, after content
   *  enrichment. Delta is computed internally. */
  afterScore: number;
  /** Optional note rendered in a highlighted strip below the score panels. Pre-translated by the caller. */
  scoreNote?: string;
  /** Overrides the outer frame. Defaults to the standalone card treatment; pass
   *  `bg-transparent p-0` when embedding inside a card that already provides one,
   *  so the meter doesn't stack a third nested panel. */
  className?: string | undefined;
  /** `data-testid` root; derives `-before-value`, `-after-value`, `-delta`, `-note`. */
  testId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Clamps a score to [0, 100] so bar widths stay sane regardless of input. */
function clamp(value: number): number {
  return Math.min(100, Math.max(0, value));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Horizontal progress bar with a fixed-height track and a proportional fill.
 * Track uses `surface-date-pill` (#E9ECF5); fill colour differs for before/after.
 */
function ScoreBar({ score, variant }: { score: number; variant: 'before' | 'after' }) {
  const pct = clamp(score);
  return (
    // Track: h-5 (20px), padding p-0.5 (2px on each side), rounded-full
    <div className="bg-surface-date-pill flex h-5 w-full items-center rounded-full p-0.5">
      {/* Fill: h-3 (12px), rounded-full, width variable */}
      <div
        className={cn(
          'h-3 rounded-full transition-[width] duration-500',
          variant === 'before' ? 'bg-warning' : 'bg-primary',
        )}
        style={{ width: `${String(pct)}%` }}
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
}

/**
 * Single score panel: label, numeric score with `/100` suffix, delta chip (after only), and a progress bar.
 */
function ScorePanel({
  label,
  score,
  variant,
  testId,
}: {
  label: string;
  score: number;
  variant: 'before' | 'after';
  delta?: number;
  testId: string;
}) {
  const { t } = useTranslation('common');

  return (
    <div data-testid={testId} className="flex flex-1 flex-col gap-2">
      <p className="text-foreground text-md font-medium">{label}</p>

      <div className="flex flex-1 flex-row items-center gap-8">
        <ScoreBar score={score} variant={variant} />

        <span>
          <span
            data-testid={`${testId}-value`}
            className={cn(
              'text-foreground text-2xl font-bold',
              variant === 'after' ? 'text-primary' : 'text-warning',
            )}
          >
            {score.toFixed(2)}
          </span>
          <span className="text-icon-date-pill text-xs font-medium">
            {t('qualityScoreMeter.outOf')}
          </span>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Displays before/after quality scores side-by-side with proportional progress
 * bars and an internally computed delta. An optional note appears in a
 * highlighted strip below the score panels.
 */
export function QualityScoreMeter({
  className,
  beforeScore: bs,
  afterScore: as,
  scoreNote,
  testId = 'quality-score-meter',
}: QualityScoreMeterProps) {
  const beforeScore = bs * 10;
  const afterScore = as * 10;

  const { t } = useTranslation('common');
  const delta = afterScore - beforeScore;

  return (
    // Outer wrapper: bg-selected-row (#F0F3FC) acts as a subtle inset border
    <div data-testid={testId} className={cn('bg-selected-row rounded-xl p-4', className)}>
      {/* Inner card */}
      <div className="bg-background flex flex-col gap-3 rounded-xl p-4">
        {/* Score panels row */}
        <div className="flex items-center gap-8">
          <ScorePanel
            label={t('qualityScoreMeter.beforeLabel')}
            score={beforeScore}
            variant="before"
            testId={`${testId}-before`}
          />

          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center gap-1">
              {!!delta && delta !== 0 && (
                <span
                  data-testid={`${testId}-delta`}
                  className={cn(
                    'flex flex-row items-center gap-1 rounded-sm px-1.5 py-1 text-xs font-semibold',
                    delta > 0 ? 'text-success' : 'text-destructive',
                    delta > 0 ? 'bg-success-50' : 'bg-error-50',
                  )}
                >
                  {delta > 0 && <ArrowUp className="h-3 w-3" />}
                  {delta < 0 && <ArrowDown className="h-3 w-3" />}

                  {delta > 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2)}
                </span>
              )}
            </div>

            {/* Arrow — 32×32, bg #F0F3FC (bg-selected-row), rounded-md, padding sm */}
            <div className="bg-selected-row mt-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-md p-1">
              <ArrowRight className="text-foreground h-4 w-4" />
            </div>
          </div>

          <ScorePanel
            label={t('qualityScoreMeter.afterLabel')}
            score={afterScore}
            variant="after"
            delta={delta}
            testId={`${testId}-after`}
          />
        </div>

        {/* Optional note strip */}
        {scoreNote && (
          <div className="bg-surface-blue rounded-xl p-3">
            <p data-testid={`${testId}-note`} className="text-text-date-pill text-xs font-light">
              {scoreNote}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
